import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, Check } from "lucide-react";
import { api, apiError, fmtDate, inr } from "../lib/api";
import { Card } from "./ui";

/**
 * Home-screen card with the player's unpaid match charges. Stays until the
 * charges are paid (marked paid by the player, or covered by a top-up).
 */
export default function PendingPaymentsCard({ total, charges, club, onPaid }) {
  const nav = useNavigate();
  const isAdmin = ["owner", "admin", "manager"].includes(club?.my_role);
  const [marking, setMarking] = useState(null);

  if (!total || total <= 0) return null;

  const markPaid = async (c) => {
    if (!window.confirm(`Mark ${inr(c.outstanding)} for "${c.description}" as paid?`)) return;
    setMarking(c.id);
    try {
      await api.post(`/transactions/${c.id}/mark-paid`);
      toast.success("Marked as paid ✓");
      onPaid?.();
    } catch (err) { toast.error(apiError(err)); }
    finally { setMarking(null); }
  };

  const upiLink = club?.upi_id
    ? `upi://pay?pa=${encodeURIComponent(club.upi_id)}&pn=${encodeURIComponent(club.name || "Club")}&am=${total.toFixed(2)}&cu=INR&tn=${encodeURIComponent("Match charges")}`
    : null;

  return (
    <div className="px-5 mt-4" data-testid="pending-payments">
      <Card className="p-4 border-red-100">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-2xl bg-red-50 text-red-500 grid place-items-center shrink-0">
              <AlertCircle size={20} />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-ink-muted font-semibold">Pending payment</div>
              <div className="text-2xl font-extrabold text-red-500 tabular-nums" data-testid="pending-total">{inr(total)}</div>
            </div>
          </div>
          {upiLink && (
            <a href={upiLink} className="h-10 px-4 rounded-2xl bg-brand text-white text-sm font-semibold grid place-items-center shrink-0 no-underline" data-testid="pay-upi-btn">
              Pay via UPI
            </a>
          )}
        </div>

        <div className="divide-y divide-black/[0.06] mt-3">
          {charges.map((c) => (
            <div key={c.id} className="flex items-center gap-3 py-2.5">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{c.description || c.category}</div>
                <div className="text-xs text-ink-muted">
                  {fmtDate(c.created_at)}
                  {c.outstanding < c.amount && ` • ${inr(c.amount - c.outstanding)} covered by wallet`}
                </div>
              </div>
              <div className="text-sm font-bold text-red-500 tabular-nums">{inr(c.outstanding)}</div>
              <button
                onClick={() => markPaid(c)}
                disabled={marking === c.id}
                className="h-8 px-2.5 rounded-xl bg-brand-50 text-brand text-xs font-semibold flex items-center gap-1 disabled:opacity-50"
                data-testid={`mark-paid-${c.id}`}
              >
                <Check size={13} /> {marking === c.id ? "…" : "Paid"}
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-2 text-xs">
          <span className="text-ink-muted">{upiLink ? "After paying, tap Paid on each charge." : "Paid the organiser? Tap Paid on each charge."}</span>
          {isAdmin && <button onClick={() => nav("/ledger")} className="font-semibold text-brand shrink-0">Ledger</button>}
        </div>
      </Card>
    </div>
  );
}
